import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { MapPin, DollarSign, Upload, X, Loader2, Trees, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteSettings } from "@/contexts/SettingsContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AMENITY_OPTIONS = [
  { key: "water", label: "Eau" },
  { key: "electricity_15a", label: "Électricité 15A (standard)" },
  { key: "electricity_30a", label: "Électricité 30A" },
  { key: "electricity_50a", label: "Électricité 50A" },
  { key: "ev_charger", label: "Borne de recharge VE" },
  { key: "sewer", label: "Egout" },
  { key: "toilet", label: "Toilette" },
  { key: "shower", label: "Douche" },
  { key: "wifi", label: "WiFi" },
  { key: "fire_pit", label: "Feu de camp" },
];

export default function CreateSpacePage() {
  const navigate = useNavigate();
  const { spaceId } = useParams();
  const { user } = useAuth();
  const { settings } = useSiteSettings();
  const minPricePerNight = parseFloat(settings?.min_price_per_night || 0);
  const isEdit = !!spaceId;
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [checkingSub, setCheckingSub] = useState(!isEdit); // only check on create
  const [photos, setPhotos] = useState([]);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    title: "", description: "", address: "", city: "", region: "", postal_code: "",
    price_per_night: "", weekly_price: "", monthly_price: "",
    terrain_type: "rv", terrain_types: ["rv"], max_vehicle_length: "", surface_type: "grass", max_spots: "1",
    image_url: "", check_in_time: "14:00", check_out_time: "11:00",
    is_free: false,
  });
  const [amenities, setAmenities] = useState({});
  const [rules, setRules] = useState({ pets_allowed: true, campfire_allowed: true, quiet_hours: "22:00-07:00" });

  // Onboarding gate (admins exempt, edit mode skips)
  useEffect(() => {
    if (isEdit || user?.role === "admin") { setCheckingSub(false); return; }
    Promise.all([
      api.get("/auth/me").then(r => r.data),
      api.get("/subscription/status").then(r => r.data),
      api.get("/connect/status").then(r => r.data),
    ]).then(([me, sub, conn]) => {
      const verifStatus = me?.identity_verification?.status || "unverified";
      const subActive = sub?.status === "active";
      const stripeReady = conn?.connected && conn?.charges_enabled && conn?.payouts_enabled;

      if (!subActive || verifStatus !== "verified" || !stripeReady) {
        toast.info("Complétez votre configuration d'hôte pour publier un terrain");
        navigate("/host-onboarding", { replace: true });
      } else {
        setCheckingSub(false);
      }
    }).catch(() => setCheckingSub(false));
  }, [isEdit, user, navigate]);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/spaces/${spaceId}`).then(({ data }) => {
      setForm({
        title: data.title || "",
        description: data.description || "",
        address: data.address || "",
        city: data.city || "",
        region: data.region || "",
        postal_code: data.postal_code || "",
        price_per_night: data.price_per_night?.toString() || "",
        weekly_price: data.weekly_price?.toString() || "",
        monthly_price: data.monthly_price?.toString() || "",
        terrain_type: data.terrain_type || "rv",
        terrain_types: data.terrain_types && data.terrain_types.length > 0 ? data.terrain_types : [data.terrain_type || "rv"],
        max_vehicle_length: data.max_vehicle_length || "",
        surface_type: data.surface_type || "grass",
        max_spots: data.max_spots?.toString() || "1",
        image_url: data.image_url || "",
        check_in_time: data.check_in_time || "14:00",
        check_out_time: data.check_out_time || "11:00",
        is_free: !!data.is_free,
      });
      setPhotos(data.photos || []);
      setAmenities(data.amenities || {});
      setRules({
        pets_allowed: data.rules?.pets_allowed ?? true,
        campfire_allowed: data.rules?.campfire_allowed ?? true,
        quiet_hours: data.rules?.quiet_hours || "22:00-07:00",
      });
    }).catch(() => toast.error("Impossible de charger le terrain"))
      .finally(() => setLoadingData(false));
  }, [spaceId, isEdit]);

  const update = (field) => (e) => setForm(f => ({ ...f, [field]: e?.target?.value ?? e }));
  const toggleAmenity = (key) => setAmenities(a => ({ ...a, [key]: !a[key] }));

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name}: trop volumineux (max 5MB)`); continue; }
        const formData = new FormData();
        formData.append("file", file);
        const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos(prev => [...prev, data.url]);
      }
      toast.success("Photos uploadees!");
    } catch (err) { toast.error("Erreur lors de l'upload"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removePhoto = (idx) => setPhotos(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isAdminFree = form.is_free && user?.role === "admin";
    if (!form.title || !form.address || !form.city || !form.postal_code || (!form.price_per_night && !isAdminFree)) {
      toast.error("Remplissez tous les champs obligatoires (titre, adresse, ville, code postal, prix)");
      return;
    }
    if (!isAdminFree && minPricePerNight > 0 && parseFloat(form.price_per_night) < minPricePerNight) {
      toast.error(`Le prix par nuit doit être d'au moins ${minPricePerNight.toFixed(2)} $.`);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        price_per_night: isAdminFree ? 0 : parseFloat(form.price_per_night),
        weekly_price: form.weekly_price && !isAdminFree ? parseFloat(form.weekly_price) : null,
        monthly_price: form.monthly_price && !isAdminFree ? parseFloat(form.monthly_price) : null,
        max_spots: parseInt(form.max_spots) || 1,
        amenities, rules, photos,
        image_url: photos[0] || form.image_url,
        check_in_time: form.check_in_time,
        check_out_time: form.check_out_time,
        is_free: isAdminFree,
      };
      await (isEdit ? api.put(`/spaces/${spaceId}`, payload) : api.post("/spaces", payload));
      toast.success(isEdit ? "Terrain mis a jour!" : "Terrain publie!");
      navigate("/my-spaces");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 animate-fade-in">
      <Card className="border border-zinc-200 rounded-2xl shadow-sm" data-testid="create-space-card">
        <CardHeader>
          <CardTitle className="font-heading text-2xl font-bold">{isEdit ? "Modifier le terrain" : "Publier un terrain"}</CardTitle>
          <CardDescription>{isEdit ? "Mettez a jour les informations de votre terrain" : "Decrivez votre emplacement pour campeurs VR, tente ou van"}</CardDescription>
        </CardHeader>
        <CardContent>
          {(loadingData || checkingSub) ? (
            <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 text-emerald-600 animate-spin" /></div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="create-space-form">
            {/* Photos */}
            <div>
              <Label>Photos de votre terrain</Label>
              <div className="mt-2 space-y-2">
                {photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {photos.map((p, i) => (
                      <div key={i} className="relative rounded-lg overflow-hidden aspect-video border border-zinc-200">
                        <img src={p.startsWith("/api/") ? `${BACKEND_URL}${p}` : p} alt="" className="w-full h-full object-cover" />
                        <Button type="button" variant="ghost" size="icon" className="absolute top-1 right-1 bg-white/90 h-6 w-6 rounded-full" onClick={() => removePhoto(i)} data-testid={`remove-photo-${i}`}><X className="h-3 w-3" /></Button>
                      </div>
                    ))}
                  </div>
                )}
                <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-zinc-300 rounded-xl cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/30 transition-colors" data-testid="upload-area">
                  {uploading ? <Loader2 className="h-8 w-8 text-emerald-600 animate-spin" /> : (
                    <><Upload className="h-8 w-8 text-zinc-400 mb-2" /><span className="text-sm text-zinc-500">Cliquez pour ajouter des photos</span><span className="text-xs text-zinc-400">JPG, PNG, WebP (max 5 MB chaque)</span></>
                  )}
                  <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileUpload} disabled={uploading} data-testid="file-input" />
                </label>
              </div>
            </div>

            <div><Label>Titre *</Label><Input value={form.title} onChange={update("title")} placeholder="Ex: Terrain boise avec branchements VR" required className="mt-1 rounded-xl" data-testid="space-title-input" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={update("description")} placeholder="Decrivez votre terrain, l'environnement, ce qui le rend unique..." className="mt-1 rounded-xl min-h-[100px]" data-testid="space-description-input" /></div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><Label>Adresse *</Label><Input value={form.address} onChange={update("address")} placeholder="123 chemin Nature" required className="mt-1 rounded-xl" data-testid="space-address-input" /></div>
              <div><Label>Ville *</Label><Input value={form.city} onChange={update("city")} placeholder="Saint-Sauveur" required className="mt-1 rounded-xl" data-testid="space-city-input" /></div>
              <div><Label>Code postal *</Label><Input value={form.postal_code} onChange={update("postal_code")} placeholder="J0R 1R0" required className="mt-1 rounded-xl uppercase" maxLength={7} data-testid="space-postal-input" /></div>
              <div className="sm:col-span-2"><Label>Région</Label><Input value={form.region} onChange={update("region")} placeholder="Laurentides" className="mt-1 rounded-xl" data-testid="space-region-input" /></div>
            </div>

            {user?.role === "admin" && (
              <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-4" data-testid="admin-free-toggle">
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={form.is_free}
                    onCheckedChange={(v) => setForm(f => ({ ...f, is_free: !!v }))}
                    id="is-free-check"
                    data-testid="is-free-checkbox"
                    className="mt-0.5"
                  />
                  <div>
                    <Label htmlFor="is-free-check" className="text-sm font-semibold text-zinc-950 cursor-pointer">
                      Terrain public gratuit
                    </Label>
                    <p className="text-xs text-zinc-600 mt-1">
                      Réservé aux administrateurs. Les utilisateurs pourront réserver sans paiement. Les réservations sont auto-confirmées (pas d'approbation requise).
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className={`grid grid-cols-1 sm:grid-cols-3 gap-4 ${form.is_free && user?.role === "admin" ? "opacity-50 pointer-events-none" : ""}`}>
              <div>
                <Label>Prix / nuit ($) {!form.is_free && "*"}</Label>
                <Input
                  type="number"
                  step="0.01"
                  min={form.is_free ? "0" : (minPricePerNight > 0 ? String(minPricePerNight) : "1")}
                  value={form.price_per_night}
                  onChange={update("price_per_night")}
                  placeholder={form.is_free ? "Gratuit" : (minPricePerNight > 0 ? `${minPricePerNight.toFixed(2)} minimum` : "35.00")}
                  required={!form.is_free}
                  disabled={form.is_free}
                  className="mt-1 rounded-xl"
                  data-testid="space-price-input"
                />
                {!form.is_free && minPricePerNight > 0 && (
                  <p className={`text-xs mt-1 ${form.price_per_night && parseFloat(form.price_per_night) < minPricePerNight ? "text-red-600 font-semibold" : "text-zinc-500"}`}>
                    Prix minimum imposé par la plateforme : {minPricePerNight.toFixed(2)} $ / nuit.
                  </p>
                )}
              </div>
              <div><Label>Prix / semaine ($)</Label><Input type="number" step="0.01" value={form.weekly_price} onChange={update("weekly_price")} placeholder="200.00" disabled={form.is_free} className="mt-1 rounded-xl" data-testid="space-weekly-price" /></div>
              <div><Label>Prix / mois ($)</Label><Input type="number" step="0.01" value={form.monthly_price} onChange={update("monthly_price")} placeholder="700.00" disabled={form.is_free} className="mt-1 rounded-xl" data-testid="space-monthly-price" /></div>
            </div>

            {/* Types de véhicule acceptés (multi-sélection) */}
            <div>
              <Label>Types de véhicule acceptés *</Label>
              <p className="text-xs text-zinc-500 mb-2">Sélectionnez tous les types que vous acceptez sur votre terrain.</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { key: "rv", label: "VR / Motorisé" },
                  { key: "tent", label: "Tente" },
                  { key: "van", label: "Van aménagé" },
                  { key: "trailer", label: "Roulotte / Caravane" },
                  { key: "other", label: "Autre" },
                ].map(t => {
                  const active = (form.terrain_types || []).includes(t.key);
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => {
                        setForm(f => {
                          const cur = new Set(f.terrain_types || []);
                          if (cur.has(t.key)) cur.delete(t.key); else cur.add(t.key);
                          if (cur.size === 0) cur.add(t.key); // keep at least 1
                          const arr = Array.from(cur);
                          return { ...f, terrain_types: arr, terrain_type: arr[0] };
                        });
                      }}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${active ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-zinc-700 border-zinc-300 hover:border-emerald-400"}`}
                      data-testid={`terrain-type-pill-${t.key}`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label>Longueur max acceptée (VR / Roulotte)</Label>
                <Select
                  value={form.max_vehicle_length || "none"}
                  onValueChange={v => setForm(f => ({ ...f, max_vehicle_length: v === "none" ? "" : v }))}
                >
                  <SelectTrigger className="mt-1 rounded-xl" data-testid="max-vehicle-length-select"><SelectValue placeholder="Non applicable" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non applicable</SelectItem>
                    <SelectItem value="10-15">10 à 15 pieds</SelectItem>
                    <SelectItem value="16-25">16 à 25 pieds</SelectItem>
                    <SelectItem value="26-35">26 à 35 pieds</SelectItem>
                    <SelectItem value="36-45">36 à 45 pieds</SelectItem>
                    <SelectItem value="45+">45 pieds et plus</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Surface</Label>
                <Select value={form.surface_type} onValueChange={v => setForm(f => ({ ...f, surface_type: v }))}>
                  <SelectTrigger className="mt-1 rounded-xl" data-testid="surface-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="grass">Gazon</SelectItem><SelectItem value="gravel">Gravier</SelectItem><SelectItem value="asphalt">Asphalte</SelectItem><SelectItem value="dirt">Terre</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Nb emplacements</Label><Input type="number" min="1" value={form.max_spots} onChange={update("max_spots")} className="mt-1 rounded-xl" data-testid="max-spots-input" /></div>
            </div>

            {/* Check-in / Check-out times */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Heure d'arrivée</Label>
                <Select value={form.check_in_time} onValueChange={v => setForm(f => ({ ...f, check_in_time: v }))}>
                  <SelectTrigger className="mt-1 rounded-xl" data-testid="check-in-time-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["12:00","13:00","14:00","15:00","16:00","17:00","18:00"].map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Heure de départ</Label>
                <Select value={form.check_out_time} onValueChange={v => setForm(f => ({ ...f, check_out_time: v }))}>
                  <SelectTrigger className="mt-1 rounded-xl" data-testid="check-out-time-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["08:00","09:00","10:00","11:00","12:00","13:00","14:00"].map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Amenities */}
            <div>
              <Label className="mb-3 block">Commodites disponibles</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {AMENITY_OPTIONS.map(a => (
                  <label key={a.key} className="flex items-center gap-2 p-2 rounded-lg border border-zinc-200 cursor-pointer hover:bg-zinc-50" data-testid={`amenity-check-${a.key}`}>
                    <Checkbox checked={!!amenities[a.key]} onCheckedChange={() => toggleAmenity(a.key)} />
                    <span className="text-sm">{a.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Rules */}
            <div>
              <Label className="mb-3 block">Règles</Label>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-xl border border-zinc-200">
                  <span className="text-sm">Animaux acceptes</span>
                  <Switch checked={rules.pets_allowed} onCheckedChange={v => setRules(r => ({ ...r, pets_allowed: v }))} data-testid="pets-switch" />
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl border border-zinc-200">
                  <span className="text-sm">Feu de camp permis</span>
                  <Switch checked={rules.campfire_allowed} onCheckedChange={v => setRules(r => ({ ...r, campfire_allowed: v }))} data-testid="campfire-switch" />
                </div>
                <div className="flex items-center gap-3 p-3 rounded-xl border border-zinc-200">
                  <span className="text-sm flex-shrink-0">Heures calmes</span>
                  <Input value={rules.quiet_hours} onChange={e => setRules(r => ({ ...r, quiet_hours: e.target.value }))} placeholder="22:00-07:00" className="rounded-lg h-8 text-sm" data-testid="quiet-hours-input" />
                </div>
              </div>
            </div>

            <Button type="submit" className="w-full bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl h-12" disabled={loading || uploading} data-testid="create-space-submit-btn">
              {loading ? (isEdit ? "Enregistrement..." : "Publication...") : (isEdit ? "Enregistrer les modifications" : "Publier mon terrain")}
            </Button>
          </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
